import { QueryClient } from '@tanstack/react-query'
import { CONTENT_VERSION, PROFILE_VERSION } from './gameVersion'

/**
 * Shared cache for data hooks. A page opened a second time renders straight
 * from the cache and refreshes in the background, instead of showing
 * "Loading..." and fetching everything again.
 *
 * - staleTime 0: every time a page opens it shows the cached copy AND fetches
 *   fresh data, so an edit made elsewhere is never hidden for longer than one
 *   request. Components mounting together still share one in-flight request.
 * - refetchOnWindowFocus: coming back to a tab refreshes what's on screen, so
 *   data left open for hours doesn't need an F5.
 * - retry 1: Supabase errors are usually real (RLS, bad query), not flaky.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

/**
 * Cache keys, so hooks and invalidations agree on the same names.
 *
 * The era is part of the key on purpose: data from two eras must never share
 * a cache entry. It also means changing CONTENT_VERSION or PROFILE_VERSION
 * retires the old keys rather than serving the previous era's rows under the
 * new one - which would look like data loss rather than a stale cache.
 */
export const queryKeys = {
  playerProfiles: ['player_profiles', 'active', PROFILE_VERSION] as const,
  iconSettings: ['icon_settings'] as const,
  discordConfig: ['discord_bot_config'] as const,
  raidEvents: ['raid_events', CONTENT_VERSION] as const,
}

/**
 * Call after writing to player_profiles outside usePlayerProfiles, so the
 * cached roster picks up the change everywhere.
 */
export function invalidatePlayerProfiles(): void {
  // Prefix match: the active roster and each member's linked characters
  void queryClient.invalidateQueries({ queryKey: ['player_profiles'] })
}
